import { ChartDto, DashboardDto, PanelDto } from "./generated/api-client";

type ChartHost = {
  uiGenerationAttempts?: Array<{
    panels?: PanelDto[];
  }>;
};

/**
 * Same series = same collection, groupBy, and chart type. When groupBy and
 * type are both missing, fall back to id so distinct untitled charts on
 * different dashboards do not collapse.
 */
const seriesKey = (chart: ChartDto, collectionName: string): string => {
  const groupBy = chart.groupBy ?? "";
  const type = chart.type ?? "";
  if (groupBy || type) return `${collectionName}\0${groupBy}\0${type}`;
  return `${collectionName}\0id:${chart.id ?? chart.name ?? ""}`;
};

/**
 * Charts the generator already produced for one collection.
 *
 * Generated UI stores charts on dashboards, and a dashboard is per entity, so
 * the charts an entity owns are reachable from the analyse attempt without
 * generating anything new. The collection route never looked, which is why the
 * guest's landing screen had no visualisation while the entity's own dashboard
 * had three.
 *
 * Matching is on `chart.collections`, not on the dashboard name: the dashboard
 * name is display copy ("Projects"), while `collections` is the identifier the
 * chart actually queries. A combined-entity dashboard can hold charts for more
 * than one collection, and only the ones for this collection belong here.
 *
 * Overview also lists this collection on its figures, so a naive include
 * pulled the same pie onto the collection route and fetched the series twice.
 * Dedupe by collection+groupBy+type. Overview stays the only place a Projects
 * pie sits next to Tasks/Invoices.
 *
 * Returns an empty array rather than throwing for a collection with no charts,
 * which is the common case: the demo has 43 collections and 5 charted ones.
 */
export function chartsForCollection(
  attempt: ChartHost | undefined,
  collectionName: string | undefined,
): ChartDto[] {
  if (!attempt || !collectionName) return [];

  const seen = new Set<string>();
  const charts: ChartDto[] = [];

  for (const generation of attempt.uiGenerationAttempts ?? []) {
    for (const panel of generation?.panels ?? []) {
      for (const dashboard of (panel?.dashboards ?? []) as DashboardDto[]) {
        for (const chart of dashboard?.charts ?? []) {
          if (!chart?.collections?.includes(collectionName)) continue;
          const key = seriesKey(chart, collectionName);
          if (seen.has(key)) continue;
          seen.add(key);
          charts.push(chart);
        }
      }
    }
  }

  return charts;
}
